import {
    Breakpoints,
    Checkbox,
    CTA,
    DisplayTag,
    Fieldset,
    Infobox,
    InfoboxAppearanceValue,
    InputAppearanceValue,
    RadioButton,
    Select,
    Text,
    TextArea,
    TextInput,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'

import axios from 'axios'

import React, {useEffect, useState} from 'react'
import {Controller, FieldErrors, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {UserDataForm} from '../../types/userData'
import {RootState} from '../rootReducer'
import {Loading} from '../components/loading'
import {openAlertSystemLayer} from '../layers/launchers/AlertSystemLayerHandle'
import {
    setPersonalData,
    setRandomCode,
    setSelectedModelDisplayName,
    setSelectedModelSlug,
    showThanksPage,
} from '../../features/userData/userDataSlice'
import {AppDispatch} from '../thunk'
import {
    authProxyUserEndpoint,
    axiosInstance,
    csrfToken,
} from '../../shared/authServiceHelper'
import {openDisclaimerLayer} from '../layers/launchers/DisclaimerLayerHandle'
import {setLoading} from '../../features/general/generalSlice'
import {Province} from '../../types/province'
import {DealerData} from '../../types/dealer'
import isEmpty from 'lodash.isempty'
import {Endpoints, Global, LIMIT_TEXT_AREA} from '../../shared/variables'
import {
    defaultModel,
    eventInfoCommon,
    getDealerUserInfo,
    TrackingAction,
    trackingData,
    trackingEvent,
} from '../../shared/trackingServiceHelper'

import store from '../store'
import {
    changePreselectedDealer,
    setMyDealerCode,
    setSelectedDealerData,
} from '../../features/dealers/dealerSlice'
import {LayerForm} from '../../shared/classes'
import {
    capitalizeWords,
    getDealerAddress,
    getDealerInfoOption,
    getFetchedDealerFromCode,
    getVersionKey,
} from '../../shared/utilities'
import {ErrorSummary} from './ErrorSummary'

const FieldContainer = styled.div`
    margin: 0 0 ${(props) => props.theme.size.dynamic0100};
`

const CouponLayerForm = styled(LayerForm)`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`

const FormFieldsContainer = styled.div`
    margin: ${(props) => props.theme.size.static600} 0
        ${(props) => props.theme.size.dynamic0150};

    @media (min-width: ${Breakpoints.b560}px) {
        margin: ${(props) => props.theme.size.dynamic0300} 0 0;
    }
`
const FormFieldsContainerTitle = styled(Text)`
    margin: 0 0 ${(props) => props.theme.size.static300};
`

const FormPrivacyContainer = styled(FormFieldsContainer)`
    position: relative;
    border: 2px solid ${(props) => props.theme.colors.border.legalframe};
    border-radius: ${(props) => props.theme.size.borderRadius.small};
    padding: ${(props) => props.theme.size.dynamic0270}
        ${(props) => props.theme.size.dynamic0100}
        ${(props) => props.theme.size.dynamic0200}
        ${(props) => props.theme.size.dynamic0100};

    @media (min-width: ${Breakpoints.b560}px) {
        padding: ${(props) => props.theme.size.dynamic0150}
            ${(props) => props.theme.size.dynamic0100};
    }
`

const FormPrivacyContainerTitle = styled(Text)`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    box-sizing: border-box;
    max-width: calc(100% - ${(props) => props.theme.size.static400});
    background: ${(props) => props.theme.colors.background.primary};
    padding: 0 ${(props) => props.theme.size.static200};
    color: ${(props) => props.theme.colors.content.active};
    text-align: center;
    white-space: nowrap;
`

const DealerCard = styled.div`
    margin: 0 0 ${(props) => props.theme.size.dynamic0020};
    padding: ${(props) => props.theme.size.dynamic0050};
    box-shadow: 0 0 0 ${(props) => props.theme.size.border.default}
        ${(props) => props.theme.colors.border.default};
    border-radius: ${(props) => props.theme.size.borderRadius.input};

    legend {
        font-weight: normal;
    }
`
const DealerCardTitle = styled(Text)`
    margin-bottom: ${(props) => props.theme.size.static150};
`
const DealerDisplayTag = styled(DisplayTag)`
    border-radius: ${(props) => props.theme.size.borderRadius.displayTag};
    height: fit-content;
    margin-bottom: ${(props) => props.theme.size.static400};
    padding: ${(props) => props.theme.size.dynamic0050};
    white-space: wrap;
`
const PrivacyText = styled(Text)`
    margin: ${(props) => props.theme.size.static150} 0
        ${(props) => props.theme.size.static350};
`
const PreCompiledCTA = styled(CTA)`
    margin: ${(props) => props.theme.size.dynamic0100} 0
        ${(props) => props.theme.size.dynamic0200};
`
const CTAVum = styled(CTA)`
    display: block;
    margin: 0 0 ${(props) => props.theme.size.static400} auto;
    color: ${({theme}) => theme.colors.content.secondary};
`
const FieldTextInputUppercase = styled(FieldContainer)`
    input {
        text-transform: uppercase;
    }
`
const StyledSelectMessage = styled.div`
    position: absolute;
    left: -9999px;
`

const DealerAlertTag = styled.div`
    width: 100%;
    max-width: 100%;
    background-color: ${(props) => props.theme.colors.signal.promo};
    padding: ${(props) => props.theme.size.dynamic0050};
    border: ${(props) => props.theme.size.border.heavy} solid
        ${(props) => props.theme.colors.border.card};
    border-radius: ${(props) => props.theme.size.borderRadius.cardS};
    legend {
        font-weight: normal;
    }
`

const SELECT_ERROR_IDS = {
    models: 'coupon-model-error',
    province: 'coupon-province-error',
    dealer: 'coupon-dealer-error',
} as const

export const COUPON_REQUEST_FORM_ID = 'coupon-request-form'

const getFirstInvalidField = (
    formErrors: FieldErrors<UserDataForm>
): keyof UserDataForm | undefined => {
    if (formErrors.name) {
        return 'name'
    }
    if (formErrors.surname) {
        return 'surname'
    }
    if (formErrors.email) {
        return 'email'
    }
    if (formErrors.telephone) {
        return 'telephone'
    }
    if (formErrors.plate) {
        return 'plate'
    }
    if (formErrors.models) {
        return 'models'
    }
    if (formErrors.province) {
        return 'province'
    }
    if (formErrors.dealer) {
        return 'dealer'
    }
    if (formErrors.service) {
        return 'service'
    }

    return undefined
}

// tslint:disable-next-line:cyclomatic-complexity
export function CouponRequestForm(): React.JSX.Element {
    const {t} = useTranslation('fa-coupon')
    const dispatch = useDispatch() as AppDispatch

    const [loadingForm, setLoadingForm] = useState(false)
    const [selectedDealer, setSelectedDealer] = useState<DealerData | null>(
        null
    )
    const [showNotification, setShowNotification] = useState(false)
    const [NotificationText, setNotificationText] = useState('')

    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )
    const logged = useSelector((state: RootState) => state.general.logged)

    const configuration = useSelector(
        (state: RootState) => state.configurationPromo.configuration
    )

    const cycleID = useSelector(
        (state: RootState) => state.userData.formCycleID
    )

    const labelSubmit = useSelector(
        (state: RootState) =>
            state.configurationPromo.configuration.form.cta_submit[
                getVersionKey()
            ]
    )
    const allProvinces = useSelector(
        (state: RootState) => state.provinces.allProvinces
    )
    const additionalInfoField = useSelector(
        (state: RootState) =>
            state.configurationPromo.configuration.form.additional_info_field
    )
    const plateField = useSelector(
        (state: RootState) =>
            state.configurationPromo.configuration.form.plate_field
    )
    const modelsField = useSelector(
        (state: RootState) =>
            state.configurationPromo.configuration.form.models_field
    )
    const blacklistedCodes = useSelector(
        (state: RootState) =>
            state.configurationPromo.configuration.dealers_search_params
                .blacklisted_codes
    )
    const allDealers = useSelector(
        (state: RootState) => state.dealers.allDealers
    )
    const isDealerWeb = useSelector(
        (state: RootState) => state.general.isDealerWeb
    )
    const myDealerCode = useSelector(
        (state: RootState) => state.dealers.myDealerCode
    )
    const myDealerBranches = useSelector(
        (state: RootState) => state.dealers.myDealerBranches
    )
    const myDealerBranchesLoading = useSelector(
        (state: RootState) => state.dealers.myDealerBranchesLoading
    )
    const fetchedDealer = getFetchedDealerFromCode(
        myDealerBranches,
        myDealerCode
    )
    const hasDealerPreselection = Boolean(
        isDealerWeb &&
            myDealerCode &&
            fetchedDealer &&
            !blacklistedCodes.includes(myDealerCode)
    )
    const priceListData = useSelector(
        (state: RootState) => state.general.priceListData
    )
    const campaignId = useSelector(
        (state: RootState) => state.general.campaignId
    )

    const [hasProvinceSelection, setHasProvinceSelection] = useState(false)
    const [filteredDealers, setFilteredDealers] = useState([])
    const [provinceStatusMessage, setProvinceStatusMessage] = useState<string>(
        t('form.province.noneSelected')
    )
    const [dealerStatusMessage, setDealerStatusMessage] = useState<string>(
        t('form.dealer.noneSelected')
    )
    const [modelStatusMessage, setModelStatusMessage] = useState<string>(
        t('form.model.noneSelected')
    )

    const filterDealersByProvince = (province: string) => {
        setHasProvinceSelection(!!province)

        setFilteredDealers(
            allDealers.filter(
                (dealer: DealerData) => dealer.provinceCode === province
            )
        )
        setProvinceStatusMessage(
            province
                ? t('form.province.selected', {
                      province,
                  })
                : t('form.province.noneSelected')
        )
    }

    const saveDealerSelected = (
        dealerCodeSelected: string,
        fromAllDealers = true
    ) => {
        const dealersToSearch = fromAllDealers ? allDealers : myDealerBranches
        const selected = dealersToSearch.find(
            (dealer: DealerData) => dealer.companyCode === dealerCodeSelected
        )
        setSelectedDealer(selected)
        setDealerStatusMessage(
            selected
                ? t('form.dealer.selected', {
                      dealer: capitalizeWords(getDealerInfoOption(selected)),
                  })
                : t('form.dealer.noneSelected')
        )
    }

    const emailRegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

    const {
        register,
        handleSubmit,
        setError,
        reset,
        setValue,
        clearErrors,
        control,
        formState,
        formState: {errors},
    } = useForm<UserDataForm>({
        mode: 'onChange',
    })

    useEffect(() => {
        setSelectedDealer(null)
        setValue('dealer', '')
        setDealerStatusMessage(t('form.dealer.noneSelected'))
        if (hasDealerPreselection && myDealerBranches.length === 1) {
            saveDealerSelected(myDealerCode, false)
            setValue('dealer', myDealerCode, {shouldValidate: true})
        }
    }, [myDealerCode, myDealerBranches, hasDealerPreselection, setValue])

    useEffect(() => {
        const requiredSelects = [
            {
                errorId: SELECT_ERROR_IDS.models,
                hasError: Boolean(errors.models),
                name: 'models',
            },
            {
                errorId: SELECT_ERROR_IDS.province,
                hasError: Boolean(errors.province),
                name: 'province',
            },
            {
                errorId: SELECT_ERROR_IDS.dealer,
                hasError: Boolean(errors.dealer),
                name: 'dealer',
            },
        ]

        requiredSelects.forEach(({name, errorId, hasError}) => {
            const select = document.querySelector<HTMLSelectElement>(
                `select[name="${name}"]`
            )

            if (!select) {
                return
            }

            select.setAttribute('aria-required', 'true')

            if (hasError) {
                select.setAttribute('aria-describedby', errorId)
            } else {
                select.removeAttribute('aria-describedby')
            }
        })
    }, [errors.models, errors.province, errors.dealer])

    const focusFirstError = (formErrors: FieldErrors<UserDataForm>) => {
        const firstInvalidField = getFirstInvalidField(formErrors)

        if (!firstInvalidField) {
            return
        }

        window.setTimeout(() => {
            document
                .querySelector<HTMLElement>(
                    `[name="${String(firstInvalidField)}"]`
                )
                ?.focus()
        }, 0)
    }

    // tslint:disable-next-line:no-shadowed-variable
    const showBackendValidationForm = (field: string, errors: any) => {
        if (field in errors) {
            // @ts-ignore
            setError(field, {
                type: 'manual',
                message: errors[String(field)].map((el: string) => el),
            })
        }
    }

    const resetErrorNotification = () => {
        setShowNotification(false)
        setNotificationText('')
    }

    const createCoupon = (userData: UserDataForm) => {
        const eligibleDealers: DealerData[] = hasDealerPreselection
            ? myDealerBranches
            : allDealers
        if (
            myDealerBranchesLoading ||
            !selectedDealer ||
            blacklistedCodes.includes(selectedDealer.companyCode) ||
            !eligibleDealers.some((dealer) => dealer.id === selectedDealer.id)
        ) {
            setError('dealer', {
                type: 'required',
                message: t('form.error.required'),
            })
            return
        }
        setLoadingForm(true)

        axios
            .post(`${backendUrl}/${Endpoints.COUPONS}`, {
                name: userData.name,
                campaign_code: configuration.code,
                surname: userData.surname,
                email: userData.email,
                is_dealerweb: isDealerWeb,
                telephone: userData.telephone,
                ...(store.getState().userData.modelDisplayName && {
                    model_slug: store.getState().userData.modelSlug,
                    model_display_name:
                        store.getState().userData.modelDisplayName,
                }),
                ...(userData.plate && {
                    plate: userData.plate.toUpperCase(),
                }),
                ...(userData.additional_info && {
                    additional_info: userData.additional_info,
                }),
                company_code: selectedDealer?.companyCode,
                dealer_name: selectedDealer?.company,
                privacy_service: userData.service,
                privacy_origin: configuration.lead_inbox.privacy_origin,
                privacy_profile_vgi: userData.privacyProfileVgi,
                privacy_marketing_vgi: userData.privacyMarketingVgi,
                full_url: window.location.href,
                ...(!isEmpty(priceListData) && {
                    price_list_additional_info: priceListData,
                }),
                ...(campaignId && {
                    campaign_id: campaignId,
                }),
            })
            .then((response) => {
                setLoadingForm(false)
                dispatch(showThanksPage(true))
                dispatch(setPersonalData(userData))
                dispatch(setSelectedDealerData(selectedDealer))
                dispatch(setRandomCode(response.data.random_code))

                trackingEvent(
                    TrackingAction.VW_BASIC_FORM_SUBMISSION_SUCCESS_MESSAGE_LOAD,
                    {
                        configuration: {
                            CarlineName: defaultModel(),
                        },
                        eventInfo: {
                            ...eventInfoCommon(),
                            contentId:
                                store.getState().configurationPromo
                                    .configuration.form.title[getVersionKey()],
                        },
                        form: {
                            FormType: trackingData.FORM_TYPE,
                            FormName: `${
                                store.getState().configurationPromo
                                    .configuration.tracking_application_id
                            } - ${
                                store.getState().configurationPromo
                                    .configuration.form.title[getVersionKey()]
                            }`,
                            MarketingConsent: [
                                {
                                    marketing:
                                        store.getState().userData.personalData
                                            .privacyMarketingVgi,
                                    profilazione:
                                        store.getState().userData.personalData
                                            .privacyProfileVgi,
                                    servizio:
                                        store.getState().userData.personalData
                                            .service,
                                },
                            ],
                            FormStart: false,
                            LeadID: trackingData.LEAD_ID + response.data.id,
                            CycleID: cycleID,
                        },
                        process: {
                            StepName: trackingData.STEP_NAME,
                            StepNumber: trackingData.STEP_NUMBER,
                        },
                        user: {
                            ...getDealerUserInfo(),
                        },
                    }
                )

                resetErrorNotification()
                reset()
            })
            .catch((error) => {
                setLoadingForm(false)

                const statusCode = error?.response?.status
                const backendErrors = error?.response?.data?.errors

                if (
                    statusCode === Global.ERROR_422 &&
                    backendErrors &&
                    typeof backendErrors === 'object'
                ) {
                    Object.keys(backendErrors).map((el) => {
                        showBackendValidationForm(el, backendErrors)
                    })
                    trackingError()
                } else if (!error?.response) {
                    openAlertSystemLayer(
                        t('layers.alertSystem.textErrorNetwork')
                    )
                } else {
                    openAlertSystemLayer()
                }
            })
    }

    const onSubmit = (data: UserDataForm) => {
        resetErrorNotification()
        createCoupon(data)
        trackingBasicClick()
    }

    const onError = (formErrors: FieldErrors<UserDataForm>) => {
        trackingBasicClick()
        trackingError()
        focusFirstError(formErrors)
    }

    const preCompileForm = () => {
        if (csrfToken) {
            setLoadingForm(true)

            axiosInstance
                .get(authProxyUserEndpoint)
                .then((res) => {
                    if (res.data.given_name) {
                        setValue('name', res.data.given_name)
                        clearErrors('name')
                    }

                    if (res.data.family_name) {
                        setValue('surname', res.data.family_name)
                        clearErrors('surname')
                    }

                    if (res.data.phone_number) {
                        setValue('telephone', res.data.phone_number)
                        clearErrors('telephone')
                    }

                    if (res.data.email) {
                        setValue('email', res.data.email)
                        clearErrors('email')
                    }

                    setLoadingForm(false)
                })
                // tslint:disable-next-line:no-shadowed-variable
                .catch((errors) => {
                    dispatch(setLoading(false))
                    throw new Error(errors)
                })
        } else {
            openAlertSystemLayer()
            throw new Error('csrfToken missed')
        }
    }

    const trackingBasicClick = () => {
        trackingEvent(TrackingAction.VW_BASIC_FORM_CTA_CLICK, {
            configuration: {
                CarlineName: defaultModel(),
            },
            eventInfo: {
                ...eventInfoCommon(),
                contentId:
                    store.getState().configurationPromo.configuration.form
                        .title[getVersionKey()],
            },
            form: {
                FormName: `${
                    store.getState().configurationPromo.configuration
                        .tracking_application_id
                } - ${
                    store.getState().configurationPromo.configuration.form
                        .title[getVersionKey()]
                }`,
                FormType: trackingData.FORM_TYPE,
            },
            link: {
                name: labelSubmit,
                url: window.location.href,
            },
        })
    }

    const changeDealer = () => {
        dispatch(setMyDealerCode(''))
        dispatch(changePreselectedDealer(true))
    }

    const trackingError = () => {
        trackingEvent(TrackingAction.VW_BASIC_FORM_ERROR_MESSAGE_LOAD, {
            form: {
                ErrorFormFields: Object.keys(errors),
                CycleID: cycleID,
            },
        })
    }

    return (
        <>
            {logged ? (
                <PreCompiledCTA
                    tag="button"
                    emphasis="secondary"
                    size="small"
                    onClick={preCompileForm}
                    aria-label={t('layers.couponRequest.precompiledCtaLabel')}
                    title={t('layers.couponRequest.precompiledCtaLabel')}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t(
                                'layers.couponRequest.precompiledCtaLabel'
                            ),
                        }}
                    />
                </PreCompiledCTA>
            ) : undefined}

            <CouponLayerForm
                id={COUPON_REQUEST_FORM_ID}
                onSubmit={handleSubmit(onSubmit, onError)}
                className={!logged ? 'no-logged' : undefined}
                noValidate
            >
                <ErrorSummary
                    errors={errors}
                    isSubmitted={formState.isSubmitted}
                />
                <div>
                    <FieldContainer>
                        <TextInput
                            {...register('name', {
                                minLength: {
                                    value: Number(
                                        Global.MINiMUM_FORM_FIELD_LENGTH
                                    ),
                                    message: t('form.error.minLength'),
                                },
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.name
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.name ? errors.name.message : undefined
                            }
                            autoComplete="given-name"
                            customId="coupon-name"
                            label={t('form.name')}
                            required
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <TextInput
                            {...register('surname', {
                                minLength: {
                                    value: Number(
                                        Global.MINiMUM_FORM_FIELD_LENGTH
                                    ),
                                    message: t('form.error.minLength'),
                                },
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.surname
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.surname
                                    ? errors.surname.message
                                    : undefined
                            }
                            autoComplete="family-name"
                            customId="coupon-surname"
                            label={t('form.surname')}
                            required
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <TextInput
                            {...register('email', {
                                pattern: {
                                    value: emailRegExp,
                                    message: t('form.error.emailNotValid'),
                                },
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.email
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.email ? errors.email.message : undefined
                            }
                            autoComplete="email"
                            customId="coupon-email"
                            label={t('form.email')}
                            required
                            type="email"
                        />
                    </FieldContainer>

                    <FieldContainer>
                        <TextInput
                            {...register('telephone', {
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.telephone
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.telephone
                                    ? errors.telephone.message
                                    : undefined
                            }
                            autoComplete="tel"
                            customId="coupon-telephone"
                            label={t('form.telephone')}
                            required
                            type="tel"
                        />
                    </FieldContainer>

                    {logged ? (
                        <CTAVum
                            tag="a"
                            href="#"
                            emphasis="tertiary"
                            onClick={(e: {preventDefault: () => void}) => {
                                e.preventDefault()
                                openDisclaimerLayer()
                            }}
                            title={t('layers.disclaimer.ctaDataDisclaimer')}
                            aria-label={t(
                                'layers.disclaimer.ctaDataDisclaimer'
                            )}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'layers.disclaimer.ctaDataDisclaimer'
                                    ),
                                }}
                            />
                        </CTAVum>
                    ) : undefined}

                    {plateField.enabled ? (
                        <FieldTextInputUppercase>
                            <TextInput
                                {...register('plate', {
                                    required: {
                                        value: true,
                                        message: t('form.error.required'),
                                    },
                                })}
                                appearance={
                                    errors.plate
                                        ? ('error' as InputAppearanceValue)
                                        : ('default' as InputAppearanceValue)
                                }
                                message={
                                    errors.plate
                                        ? errors.plate.message
                                        : undefined
                                }
                                autoComplete="off"
                                customId="coupon-plate"
                                label={t('form.plate')}
                                required
                            />
                        </FieldTextInputUppercase>
                    ) : undefined}

                    {modelsField.enabled ? (
                        <>
                            <FieldContainer>
                                <Controller
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t('form.error.required'),
                                        },
                                    }}
                                    name="models"
                                    render={({field}) => {
                                        const handleModelChange = (
                                            val: React.SyntheticEvent<HTMLSelectElement>
                                        ) => {
                                            field.onChange(val)
                                            dispatch(
                                                setSelectedModelSlug(
                                                    (
                                                        val.target as HTMLInputElement
                                                    )?.value
                                                )
                                            )
                                            const modelDisplayName =
                                                // @ts-ignore
                                                val.target.options[
                                                    // @ts-ignore
                                                    val.target.selectedIndex
                                                ].text
                                            dispatch(
                                                setSelectedModelDisplayName(
                                                    modelDisplayName
                                                )
                                            )

                                            setModelStatusMessage(
                                                modelDisplayName
                                                    ? t('form.model.selected', {
                                                          model: modelDisplayName,
                                                      })
                                                    : t(
                                                          'form.model.noneSelected'
                                                      )
                                            )

                                            trackingEvent(
                                                TrackingAction.VW_BASIC_MODEL_SELECT_CLICK,
                                                {
                                                    configuration: {
                                                        ModelName: (
                                                            val.target as HTMLInputElement
                                                        )?.value,
                                                    },
                                                }
                                            )
                                        }

                                        const modelOptions = (
                                            <>
                                                <option aria-selected value="">
                                                    {t('form.models.default')}
                                                </option>

                                                {Object.keys(
                                                    modelsField.enabled_models
                                                ).map((key) => (
                                                    <option
                                                        aria-selected
                                                        value={key}
                                                        key={key}
                                                    >
                                                        {
                                                            modelsField
                                                                .enabled_models[
                                                                String(key)
                                                            ]
                                                        }
                                                    </option>
                                                ))}
                                            </>
                                        )

                                        if (errors.models) {
                                            return (
                                                <Select
                                                    appearance="error"
                                                    message={
                                                        errors.models.message ||
                                                        ''
                                                    }
                                                    name={field.name}
                                                    onBlur={field.onBlur}
                                                    onChange={handleModelChange}
                                                    value={field.value || ''}
                                                    label={t(
                                                        'form.models.label'
                                                    )}
                                                    required
                                                >
                                                    {modelOptions}
                                                </Select>
                                            )
                                        }

                                        return (
                                            <Select
                                                name={field.name}
                                                onBlur={field.onBlur}
                                                onChange={handleModelChange}
                                                value={field.value || ''}
                                                label={t('form.models.label')}
                                                required
                                            >
                                                {modelOptions}
                                            </Select>
                                        )
                                    }}
                                />
                            </FieldContainer>
                            {errors.models ? (
                                <StyledSelectMessage
                                    id={SELECT_ERROR_IDS.models}
                                    role="alert"
                                    aria-live="assertive"
                                >
                                    {errors.models.message}
                                </StyledSelectMessage>
                            ) : undefined}
                            <StyledSelectMessage
                                role="status"
                                aria-live="polite"
                            >
                                {modelStatusMessage}
                            </StyledSelectMessage>
                        </>
                    ) : undefined}

                    {additionalInfoField.enabled ? (
                        <FieldContainer>
                            <Controller
                                control={control}
                                name="additional_info"
                                render={({field: {onChange}}) => (
                                    <TextArea
                                        rows={3}
                                        maxLength={LIMIT_TEXT_AREA}
                                        onChange={(val) => {
                                            onChange(val)
                                        }}
                                        message={
                                            errors.additional_info
                                                ? errors.additional_info.message
                                                : undefined
                                        }
                                        appearance={
                                            'default' as InputAppearanceValue
                                        }
                                        label={
                                            additionalInfoField.label
                                                ? additionalInfoField.label
                                                : undefined
                                        }
                                    />
                                )}
                            />
                        </FieldContainer>
                    ) : undefined}
                </div>

                {myDealerBranchesLoading ? (
                    <Loading />
                ) : !hasDealerPreselection ? (
                    <FormFieldsContainer>
                        <FormFieldsContainerTitle
                            tag={TextTag.h3}
                            bold
                            appearance={TokenTextAppearance.headline200}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t('form.dealerInfo.title'),
                                }}
                            />
                        </FormFieldsContainerTitle>

                        {blacklistedCodes.includes(myDealerCode) ? (
                            <DealerDisplayTag
                                appearance="alert"
                                text={`${capitalizeWords(
                                    myDealerBranches?.find(
                                        (x: DealerData) =>
                                            x.companyCode ===
                                            (myDealerCode as string)
                                    )?.company ||
                                        t(
                                            'form.myDealer.notActivePromotion.defaultDealer'
                                        )
                                )} ${t(
                                    'form.myDealer.notActivePromotion.alert'
                                )}`}
                            />
                        ) : undefined}

                        {!isEmpty(allProvinces) ? (
                            <>
                                <FieldContainer>
                                    <Controller
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: t(
                                                    'form.error.required'
                                                ),
                                            },
                                        }}
                                        name="province"
                                        render={({field}) => (
                                            // @ts-ignore
                                            <Select
                                                name={field.name}
                                                onBlur={field.onBlur}
                                                onChange={(val) => {
                                                    field.onChange(val)
                                                    filterDealersByProvince(
                                                        val.currentTarget.value
                                                    )
                                                }}
                                                value={field.value || ''}
                                                label={t('form.province.label')}
                                                required
                                                {...(errors.province
                                                    ? {
                                                          appearance: 'error',
                                                          message:
                                                              errors.province
                                                                  .message ||
                                                              '',
                                                      }
                                                    : {})}
                                            >
                                                <option value="" aria-selected>
                                                    {t('form.province.default')}
                                                </option>
                                                {allProvinces.map(
                                                    (option: Province) => (
                                                        <option
                                                            aria-selected
                                                            value={option.code}
                                                            key={option.id}
                                                        >
                                                            {option.name}
                                                        </option>
                                                    )
                                                )}
                                            </Select>
                                        )}
                                    />
                                </FieldContainer>
                                {errors.province ? (
                                    <StyledSelectMessage
                                        id={SELECT_ERROR_IDS.province}
                                        role="alert"
                                        aria-live="assertive"
                                    >
                                        {errors.province.message}
                                    </StyledSelectMessage>
                                ) : undefined}
                                <StyledSelectMessage
                                    role="status"
                                    aria-live="polite"
                                >
                                    {provinceStatusMessage}
                                </StyledSelectMessage>
                            </>
                        ) : undefined}

                        {!isEmpty(allDealers) ? (
                            <>
                                {!hasProvinceSelection ||
                                filteredDealers.length > 0 ? (
                                    <>
                                        <FieldContainer>
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: {
                                                        value: true,
                                                        message: t(
                                                            'form.error.required'
                                                        ),
                                                    },
                                                }}
                                                name="dealer"
                                                render={({field}) => (
                                                    // @ts-ignore
                                                    <Select
                                                        name={field.name}
                                                        onBlur={field.onBlur}
                                                        onChange={(val) => {
                                                            field.onChange(val)
                                                            saveDealerSelected(
                                                                val
                                                                    .currentTarget
                                                                    .value
                                                            )
                                                        }}
                                                        value={
                                                            field.value || ''
                                                        }
                                                        label={t(
                                                            'form.dealer.label'
                                                        )}
                                                        required
                                                        {...(errors.dealer
                                                            ? {
                                                                  appearance:
                                                                      'error',
                                                                  message:
                                                                      errors
                                                                          .dealer
                                                                          .message ||
                                                                      '',
                                                              }
                                                            : {})}
                                                    >
                                                        <option
                                                            aria-selected
                                                            value=""
                                                        >
                                                            {t(
                                                                'form.dealer.default'
                                                            )}
                                                        </option>

                                                        {filteredDealers.map(
                                                            (
                                                                option: DealerData
                                                            ) => (
                                                                <option
                                                                    aria-selected
                                                                    key={
                                                                        option.id
                                                                    }
                                                                    value={
                                                                        option.companyCode
                                                                    }
                                                                >
                                                                    {capitalizeWords(
                                                                        getDealerInfoOption(
                                                                            option
                                                                        )
                                                                    )}
                                                                </option>
                                                            )
                                                        )}
                                                    </Select>
                                                )}
                                            />
                                        </FieldContainer>
                                        {errors.dealer ? (
                                            <StyledSelectMessage
                                                id={SELECT_ERROR_IDS.dealer}
                                                role="alert"
                                                aria-live="assertive"
                                            >
                                                {errors.dealer.message}
                                            </StyledSelectMessage>
                                        ) : undefined}
                                        <StyledSelectMessage
                                            role="status"
                                            aria-live="polite"
                                        >
                                            {dealerStatusMessage}
                                        </StyledSelectMessage>
                                    </>
                                ) : null}

                                {hasProvinceSelection &&
                                filteredDealers.length === 0 ? (
                                    <DealerAlertTag
                                        aria-live="polite"
                                        role="status"
                                    >
                                        <Text
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                            color={TokenTextColor.inverted}
                                        >
                                            {t(
                                                'form.dealer.noDealerForProvince'
                                            )}
                                        </Text>
                                    </DealerAlertTag>
                                ) : undefined}
                            </>
                        ) : undefined}
                    </FormFieldsContainer>
                ) : (
                    <FormFieldsContainer>
                        <FormFieldsContainerTitle
                            tag={TextTag.h3}
                            bold
                            appearance={TokenTextAppearance.headline200}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'form.myDealer.dealerSelectedInfo.title'
                                    ),
                                }}
                            />
                        </FormFieldsContainerTitle>

                        <DealerCard>
                            <DealerCardTitle
                                tag={TextTag.p}
                                bold
                                appearance={TokenTextAppearance.bigcopy100}
                            >
                                {myDealerBranches.length && fetchedDealer
                                    ? capitalizeWords(fetchedDealer.company)
                                    : undefined}
                            </DealerCardTitle>
                            {!isEmpty(myDealerBranches) ? (
                                <>
                                    {myDealerBranches.length > 1 ? (
                                        <Controller
                                            control={control}
                                            rules={{
                                                required: {
                                                    value: true,
                                                    message: t(
                                                        'form.error.required'
                                                    ),
                                                },
                                            }}
                                            name="dealer"
                                            render={({field}) => (
                                                <Fieldset
                                                    legend={t(
                                                        'form.myDealer.dealerSelectedRadiobutton.legend'
                                                    )}
                                                    appearance={
                                                        errors.dealer
                                                            ? ('error' as InputAppearanceValue)
                                                            : ('default' as InputAppearanceValue)
                                                    }
                                                    message={
                                                        errors.dealer
                                                            ? errors.dealer
                                                                  .message
                                                            : undefined
                                                    }
                                                >
                                                    {myDealerBranches
                                                        .filter(
                                                            (
                                                                option: DealerData
                                                            ) =>
                                                                !blacklistedCodes.includes(
                                                                    option.companyCode
                                                                )
                                                        )
                                                        .map(
                                                            (
                                                                option: DealerData
                                                            ) => (
                                                                <RadioButton
                                                                    key={
                                                                        option.id
                                                                    }
                                                                    value={
                                                                        option.companyCode
                                                                    }
                                                                    label={capitalizeWords(
                                                                        getDealerInfoOption(
                                                                            option
                                                                        )
                                                                    )}
                                                                    onChange={
                                                                        field.onChange
                                                                    }
                                                                    name="dealer"
                                                                    onClick={() => {
                                                                        saveDealerSelected(
                                                                            option.companyCode,
                                                                            false
                                                                        )
                                                                    }}
                                                                    checked={
                                                                        field.value ===
                                                                        option.companyCode
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                </Fieldset>
                                            )}
                                        />
                                    ) : (
                                        <>
                                            <span>
                                                {myDealerBranches.length &&
                                                fetchedDealer
                                                    ? capitalizeWords(
                                                          getDealerAddress(
                                                              fetchedDealer
                                                          )
                                                      )
                                                    : undefined}
                                            </span>
                                        </>
                                    )}
                                </>
                            ) : undefined}
                        </DealerCard>
                        <CTA
                            tag="a"
                            emphasis="tertiary"
                            href="#"
                            onClick={() => changeDealer()}
                            aria-label={t(
                                'form.myDealer.changeDealerCta.label'
                            )}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'form.myDealer.changeDealerCta.label'
                                    ),
                                }}
                            />
                        </CTA>
                    </FormFieldsContainer>
                )}

                <FormPrivacyContainer>
                    <FormPrivacyContainerTitle
                        tag={TextTag.h3}
                        bold
                        appearance={TokenTextAppearance.copy200}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('form.privacy.title'),
                            }}
                        />
                    </FormPrivacyContainerTitle>

                    <CTA
                        tag="a"
                        href={configuration.form.privacy_policy_url}
                        emphasis="tertiary"
                        target="_blank"
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('form.privacy.labelCTA'),
                            }}
                        />
                    </CTA>
                    <PrivacyText
                        tag={TextTag.p}
                        appearance={TokenTextAppearance.copy200}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('form.privacy.text'),
                            }}
                        />
                    </PrivacyText>
                    <Fieldset>
                        <Checkbox
                            {...register('service', {
                                required: {
                                    value: true,
                                    message: t('form.error.required'),
                                },
                            })}
                            appearance={
                                errors.service
                                    ? ('error' as InputAppearanceValue)
                                    : ('default' as InputAppearanceValue)
                            }
                            message={
                                errors.service
                                    ? errors.service.message
                                    : undefined
                            }
                            label={t('form.privacy.service')}
                            required
                            value=""
                        />
                        <Checkbox
                            {...register('privacyMarketingVgi')}
                            label={t('form.privacy.privacyMarketingVgi')}
                            value=""
                        />
                        <Checkbox
                            {...register('privacyProfileVgi')}
                            label={t('form.privacy.privacyProfileVgi')}
                            value=""
                        />
                    </Fieldset>
                </FormPrivacyContainer>

                {showNotification ? (
                    <>
                        <Infobox
                            aria-live="assertive"
                            appearance={'error' as InfoboxAppearanceValue}
                        >
                            <Text tag={TextTag.p}>{NotificationText}</Text>
                        </Infobox>
                    </>
                ) : undefined}
            </CouponLayerForm>

            {loadingForm ||
            (!hasDealerPreselection &&
                (isEmpty(allProvinces) || isEmpty(allDealers))) ? (
                <Loading />
            ) : undefined}
        </>
    )
}
